import React from 'react';
import Box from '@amzn/awsui-components-react/polaris/box';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Button from '@amzn/awsui-components-react/polaris/button';
import Link, {LinkProps} from '@amzn/awsui-components-react/polaris/link';
import {HeaderProps, TableProps} from '@amzn/awsui-components-react';
import Header from '@amzn/awsui-components-react/polaris/header';


export const DDB_PAGE_SIZE: number = 10;

export const getFilterCounterText = count => `${count} ${count === 1 ? 'match' : 'matches'}`;

export const TableNoMatchState = ({ onClearFilter }: { onClearFilter: () => void }) => (
    <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
        <SpaceBetween size="xxs">
            <div>
                <b>No matches</b>
                <Box variant="p" color="inherit">
                    No Match Found
                </Box>
            </div>
            <Button onClick={onClearFilter}>Clear filter</Button>
        </SpaceBetween>
    </Box>
);


export const TableEmptyState = ({ resourceName }: { resourceName: string }) => (
    <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
        <SpaceBetween size="xxs">
            <div>
                <b>No {resourceName.toLowerCase()}s</b>
                <Box variant="p" color="inherit">
                    No {resourceName.toLowerCase()}s associated with this resource.
                </Box>
            </div>
            <Button>Create {resourceName.toLowerCase()}</Button>
        </SpaceBetween>
    </Box>
);

export const renderCellWithLink = (stringVal: string, url: string | undefined, urlText: string) => {
    if (undefined != url) {
        return (
            <div>
                {stringVal}  (<Link href={url}>{urlText}</Link>)
            </div>
        );
    }
    else {
        return (
            <div>
                {stringVal}
            </div>
        );
    }
}

export function createTableSortLabelFn(column: TableProps.ColumnDefinition<unknown>): TableProps.ColumnDefinition<unknown>['ariaLabel'] {
    if (!column.sortingField && !column.sortingComparator && !column.ariaLabel) {
        return;
    }
    return ({ sorted, descending }) => {
        return `${column.header}, ${sorted ? `sorted ${descending ? 'descending' : 'ascending'}` : 'not sorted'}.`;
    };
}

export const getTextFilterCounterText = (count: number | undefined) => `${count} ${count === 1 ? 'match' : 'matches'}`;

interface FullPageHeaderProps extends HeaderProps {
    title?: string;
    createButtonText?: string;
    extraActions?: React.ReactNode;
    selectedItemsCount: number;
    onInfoLinkClick?: () => void;
    onCreateButtonClick?: (event: CustomEvent) => void;
    onViewDetailsButtonClick?: (event: CustomEvent) => void;
    onEditDetailsButtonClick?: (event: CustomEvent) => void;
    disableDelete?: boolean;
    disableEdit?: boolean;
}

interface InfoLinkProps {
    id?: string;
    ariaLabel?: string;
    onFollow: LinkProps['onFollow'];
}
export const InfoLink = (props: InfoLinkProps) => (
    <Link variant="info" {...props}>
        Info
    </Link>
);

export function TableWithFullPageHeader({
                                            title = 'Samples',
                                            createButtonText = 'Create Sample',
                                            extraActions = null,
                                            selectedItemsCount,
                                            onInfoLinkClick,
                                            onCreateButtonClick,
                                            onViewDetailsButtonClick,
                                            onEditDetailsButtonClick,
                                            disableDelete,
                                            disableEdit,
                                            ...props
                                        }: FullPageHeaderProps) {
    const isOnlyOneSelected = selectedItemsCount === 1;

    return (
        <Header
            variant="awsui-h1-sticky"
            info={onInfoLinkClick && <InfoLink onFollow={onInfoLinkClick} />}
            actions={
                <SpaceBetween size="xs" direction="horizontal">
                    {extraActions}
                    <Button disabled={!isOnlyOneSelected} onClick={onViewDetailsButtonClick}>
                        View details
                    </Button>
                    {!disableEdit && <Button disabled={!isOnlyOneSelected} onClick={onEditDetailsButtonClick}>
                        Edit
                    </Button>}
                    {!disableDelete && <Button disabled={selectedItemsCount === 0}>
                        Delete
                    </Button>}
                    <Button variant="primary" onClick={onCreateButtonClick}>
                        {createButtonText}
                    </Button>
                </SpaceBetween>
            }
            {...props}
        >
            {title}
        </Header>
    );
}

export const getHeaderCounterText = (
    items: ReadonlyArray<unknown>,
    selectedItems: ReadonlyArray<unknown> | undefined
) => {
    return selectedItems && selectedItems?.length > 0 ? `(${selectedItems.length}/${items.length})` : `(${items.length})`;
};
